import { useMemo } from "react";
import { buildCollapseAmountRows } from "@platform/formatting-utils";
import { buildQuotesCellFormatOverride } from "@platform/mpx-quotes-grid";

import { buildQuotesCellEditorOverride } from "../formatting/cellEditorOverride";
import { buildQuotesCellViewRenderer } from "../formatting/quotesCellViewRenderer";

import type { CoverageTypeEnum } from "@platform/types";
import type { QuotesPopupCustomTextOptions } from "../formatting/cellEditorOverride";
import type {
  CellEditorOverride,
  CellFormatOverride,
  CellViewRendererOverride,
} from "@platform/btx-plan-designer";

interface QuotesOverrides {
  cellFormatOverride: CellFormatOverride | null;
  cellEditorOverride: CellEditorOverride | null;
  cellViewRendererOverride: CellViewRendererOverride | null;
  collapseAmountRows: (rowData: unknown[]) => unknown[];
}

/**
 * Builds and memoizes the three cell-override factories and the row
 * collapse transform for the Quotes tab. All are keyed on the selected
 * coverage type and rebuild when it changes.
 */
export const useQuotesOverrides = (
  selectedCoverageType: CoverageTypeEnum | undefined,
  customTextOptions?: QuotesPopupCustomTextOptions,
  comaInclusionOnly = false,
): QuotesOverrides => {
  const cellFormatOverride = useMemo(
    () => buildQuotesCellFormatOverride(selectedCoverageType),
    [selectedCoverageType],
  );

  const cellEditorOverride = useMemo(
    () =>
      buildQuotesCellEditorOverride(selectedCoverageType, customTextOptions),
    [customTextOptions, selectedCoverageType],
  );

  const cellViewRendererOverride = useMemo(
    () =>
      buildQuotesCellViewRenderer(
        selectedCoverageType,
        customTextOptions?.customTextByCellKey,
        comaInclusionOnly,
      ),
    [
      comaInclusionOnly,
      customTextOptions?.customTextByCellKey,
      selectedCoverageType,
    ],
  );

  const collapseAmountRows = useMemo(
    () => buildCollapseAmountRows(selectedCoverageType),
    [selectedCoverageType],
  );

  return {
    cellFormatOverride,
    cellEditorOverride,
    cellViewRendererOverride,
    collapseAmountRows,
  };
};
